import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Phone, FileText, TrendingUp, Search, Filter,
  PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock,
  ChevronRight, RefreshCw, LogOut, Building, Calendar,
  DollarSign, CheckCircle, XCircle, AlertCircle, Play,
  Sparkles, MessageCircle, MapPin, Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Stage configuration
const STAGE_CONFIG = {
  new_lead: { label: 'New Lead', color: '#3b82f6', icon: Sparkles },
  attempting_contact: { label: 'Attempting Contact', color: '#f59e0b', icon: PhoneOutgoing },
  contacted: { label: 'Contacted', color: '#8b5cf6', icon: MessageCircle },
  site_visit_scheduled: { label: 'Site Visit Scheduled', color: '#06b6d4', icon: Calendar },
  estimate_sent: { label: 'Estimate Sent', color: '#ec4899', icon: FileText },
  won: { label: 'Won', color: '#22c55e', icon: CheckCircle },
  lost: { label: 'Lost', color: '#ef4444', icon: XCircle }
};

// Source icons
const SOURCE_ICONS = {
  google_ads: { label: 'Google Ads', color: '#4285f4' },
  facebook_ads: { label: 'Facebook Ads', color: '#1877f2' },
  instagram_ads: { label: 'Instagram Ads', color: '#e4405f' },
  website_form: { label: 'Website Form', color: '#10b981' },
  landing_page: { label: 'Landing Page', color: '#8b5cf6' },
  phone_call: { label: 'Phone Call', color: '#f59e0b' },
  referral: { label: 'Referral', color: '#06b6d4' },
  direct: { label: 'Direct', color: '#6b7280' },
  other: { label: 'Other', color: '#9ca3af' }
};

export default function ClientLeadPortal() {
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/api/client/me`, { credentials: 'include' });
      if (!res.ok) {
        navigate('/portal/login');
        return;
      }
      const data = await res.json();
      setClientInfo(data);
      fetchData();
    } catch (err) {
      navigate('/portal/login');
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, callsRes, formsRes] = await Promise.all([
        fetch(`${API}/api/portal/stats`, { credentials: 'include' }),
        fetch(`${API}/api/portal/leads?limit=100`, { credentials: 'include' }),
        fetch(`${API}/api/portal/calls?limit=50`, { credentials: 'include' }),
        fetch(`${API}/api/portal/forms?limit=50`, { credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData.leads || []);
      }
      if (callsRes.ok) {
        const callsData = await callsRes.json();
        setCalls(callsData.calls || []);
      }
      if (formsRes.ok) {
        const formsData = await formsRes.json();
        setForms(formsData.forms || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/api/client/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    localStorage.removeItem('client_logged_in');
    navigate('/portal/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (!value) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = !searchQuery || 
      lead.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone?.includes(searchQuery);
    
    const matchesStage = !stageFilter || lead.pipeline_stage === stageFilter;
    
    return matchesSearch && matchesStage;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800">{clientInfo?.business_name || 'Lead Portal'}</h1>
                <p className="text-xs text-slate-500">Lead Dashboard</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={fetchData}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-slate-500" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-sm text-slate-500">Total Leads</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.leads?.total || 0}</p>
            <p className="text-sm text-slate-500 mt-1">
              <span className="text-green-600 font-medium">+{stats?.leads?.this_week || 0}</span> this week
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <span className="text-sm text-slate-500">This Month</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.leads?.this_month || 0}</p>
            <p className="text-sm text-slate-500 mt-1">New leads</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5 text-purple-500" />
              </div>
              <span className="text-sm text-slate-500">Call Logs</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.calls || 0}</p>
            <p className="text-sm text-slate-500 mt-1">Total recorded</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white border border-slate-200 rounded-2xl p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-sm text-slate-500">Form Submissions</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.forms || 0}</p>
            <p className="text-sm text-slate-500 mt-1">Total captured</p>
          </motion.div>
        </div>

        {/* Pipeline Mini-View */}
        {stats?.pipeline && Object.keys(stats.pipeline).length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="font-semibold text-slate-800 mb-4">Pipeline Overview</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.pipeline).map(([stage, count]) => {
                const config = STAGE_CONFIG[stage] || { label: stage, color: '#6b7280' };
                return (
                  <button
                    key={stage}
                    onClick={() => {
                      setStageFilter(stage);
                      setActiveTab('leads');
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 hover:border-blue-300 transition-colors"
                  >
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                    <span className="text-sm text-slate-700">{config.label}</span>
                    <span className="text-sm font-bold" style={{ color: config.color }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="border-b border-slate-200 px-4">
            <div className="flex gap-1">
              {[
                { id: 'leads', label: 'Leads', icon: Users, count: leads.length },
                { id: 'calls', label: 'Call Log', icon: Phone, count: calls.length },
                { id: 'forms', label: 'Form Submissions', icon: FileText, count: forms.length }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {/* Leads Tab */}
            {activeTab === 'leads' && (
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                    />
                  </div>
                  <select
                    value={stageFilter}
                    onChange={(e) => setStageFilter(e.target.value)}
                    className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                  >
                    <option value="">All Stages</option>
                    {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                  {stageFilter && (
                    <button
                      onClick={() => setStageFilter('')}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Clear filter
                    </button>
                  )}
                </div>

                {/* Leads List */}
                <div className="space-y-2">
                  {filteredLeads.length > 0 ? (
                    filteredLeads.map((lead) => {
                      const stageConfig = STAGE_CONFIG[lead.pipeline_stage] || { label: lead.pipeline_stage, color: '#6b7280' };
                      const sourceConfig = SOURCE_ICONS[lead.source] || { label: 'Other', color: '#9ca3af' };
                      const StageIcon = stageConfig.icon || AlertCircle;

                      return (
                        <motion.div
                          key={lead.lead_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl p-4 cursor-pointer transition-colors"
                          onClick={() => setSelectedLead(lead)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-slate-800">
                                  {lead.first_name} {lead.last_name}
                                </h3>
                                <span
                                  className="px-2 py-0.5 text-xs font-medium rounded-full"
                                  style={{
                                    backgroundColor: `${stageConfig.color}15`,
                                    color: stageConfig.color
                                  }}
                                >
                                  {stageConfig.label}
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                                  {lead.phone}
                                </span>
                                {lead.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                                    {lead.email}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-3 mt-2">
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                                  style={{ backgroundColor: `${sourceConfig.color}15`, color: sourceConfig.color }}
                                >
                                  {sourceConfig.label}
                                </span>
                                {lead.service_type && (
                                  <span className="text-xs text-slate-500">{lead.service_type}</span>
                                )}
                                {lead.deal_value && (
                                  <span className="text-xs font-medium text-green-600">
                                    {formatCurrency(lead.deal_value)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="text-right flex-shrink-0">
                              <p className="text-xs text-slate-400">{formatDate(lead.created_at)}</p>
                              <ChevronRight className="w-4 h-4 text-slate-400 mt-2 ml-auto" />
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No leads found</p>
                      <p className="text-sm text-slate-400">Leads will appear here as they come in</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Calls Tab */}
            {activeTab === 'calls' && (
              <div className="space-y-3">
                {calls.length > 0 ? (
                  calls.map((call) => (
                    <div
                      key={call.call_id}
                      className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            call.answered ? 'bg-green-50' : call.missed ? 'bg-red-50' : 'bg-amber-50'
                          }`}>
                            {call.answered ? (
                              <PhoneIncoming className="w-5 h-5 text-green-500" />
                            ) : call.missed ? (
                              <PhoneMissed className="w-5 h-5 text-red-500" />
                            ) : (
                              <Phone className="w-5 h-5 text-amber-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{call.caller_number}</p>
                            {call.caller_name && (
                              <p className="text-sm text-slate-500">{call.caller_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            call.answered 
                              ? 'bg-green-100 text-green-700' 
                              : call.missed 
                                ? 'bg-red-100 text-red-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {call.answered ? 'Answered' : call.missed ? 'Missed' : 'Voicemail'}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(call.call_started_at)}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          {formatDuration(call.duration_seconds)}
                        </span>
                        {call.source && (
                          <span className="text-slate-500">Source: {call.source}</span>
                        )}
                        {call.campaign && (
                          <span className="text-slate-500">Campaign: {call.campaign}</span>
                        )}
                      </div>

                      {call.recording_url && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <audio controls className="w-full h-8">
                            <source src={call.recording_url} type="audio/mpeg" />
                          </audio>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No call recordings yet</p>
                    <p className="text-sm text-slate-400">Call recordings will appear here once connected</p>
                  </div>
                )}
              </div>
            )}

            {/* Forms Tab */}
            {activeTab === 'forms' && (
              <div className="space-y-3">
                {forms.length > 0 ? (
                  forms.map((form) => (
                    <div
                      key={form.submission_id}
                      className="bg-slate-50 border border-slate-200 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-800">{form.form_name}</p>
                          {form.source_url && (
                            <p className="text-sm text-slate-500 truncate max-w-md">{form.source_url}</p>
                          )}
                        </div>
                        <p className="text-xs text-slate-400">{formatDate(form.submitted_at)}</p>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(form.data || {}).slice(0, 6).map(([key, value]) => (
                          <div key={key}>
                            <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-slate-700 truncate">{String(value)}</p>
                          </div>
                        ))}
                      </div>

                      {form.utm_source && (
                        <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3 text-xs text-slate-500">
                          {form.utm_source && <span>Source: {form.utm_source}</span>}
                          {form.utm_medium && <span>Medium: {form.utm_medium}</span>}
                          {form.utm_campaign && <span>Campaign: {form.utm_campaign}</span>}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No form submissions yet</p>
                    <p className="text-sm text-slate-400">Form submissions will appear here as they come in</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {selectedLead && (
          <LeadDetailModal
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Lead Detail Modal for Portal
function LeadDetailModal({ lead, onClose }) {
  const [fullLead, setFullLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeadDetails();
  }, [lead]);

  const fetchLeadDetails = async () => {
    try {
      const res = await fetch(`${API}/api/portal/leads/${lead.lead_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setFullLead(data);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stageConfig = STAGE_CONFIG[lead.pipeline_stage] || { label: lead.pipeline_stage, color: '#6b7280' };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {lead.first_name} {lead.last_name}
              </h2>
              <span
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full mt-1"
                style={{
                  backgroundColor: `${stageConfig.color}15`,
                  color: stageConfig.color
                }}
              >
                {stageConfig.label}
              </span>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <XCircle className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Contact Info */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Contact Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{fullLead?.lead?.phone || lead.phone}</span>
                  </div>
                  {(fullLead?.lead?.email || lead.email) && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <span>{fullLead?.lead?.email || lead.email}</span>
                    </div>
                  )}
                  {fullLead?.lead?.address && (
                    <div className="flex items-start gap-3 text-slate-600">
                      <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                      <span>
                        {fullLead.lead.address}
                        {fullLead.lead.city && `, ${fullLead.lead.city}`}
                        {fullLead.lead.state && `, ${fullLead.lead.state}`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Lead Info */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Lead Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Source</p>
                    <p className="text-slate-700 capitalize">
                      {(fullLead?.lead?.source || lead.source || 'Unknown').replace(/_/g, ' ')}
                    </p>
                  </div>
                  {(fullLead?.lead?.service_type || lead.service_type) && (
                    <div>
                      <p className="text-xs text-slate-500">Service Type</p>
                      <p className="text-slate-700">{fullLead?.lead?.service_type || lead.service_type}</p>
                    </div>
                  )}
                  {(fullLead?.lead?.deal_value || lead.deal_value) && (
                    <div>
                      <p className="text-xs text-slate-500">Estimated Value</p>
                      <p className="text-slate-700 font-medium">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(fullLead?.lead?.deal_value || lead.deal_value)}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-slate-700">
                      {new Date(fullLead?.lead?.created_at || lead.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Call Logs */}
              {fullLead?.calls && fullLead.calls.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Recent Calls</h3>
                  <div className="space-y-2">
                    {fullLead.calls.slice(0, 3).map((call) => (
                      <div key={call.call_id} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${call.answered ? 'text-green-600' : 'text-red-500'}`}>
                            {call.answered ? 'Answered' : 'Missed'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {new Date(call.call_started_at).toLocaleString()}
                          </span>
                        </div>
                        {call.recording_url && (
                          <audio controls className="w-full h-8 mt-2">
                            <source src={call.recording_url} type="audio/mpeg" />
                          </audio>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
